export class ShareGenerator {
    static CATEGORY_COLORS = [
        '🟦', // Blue square
        '🟨', // Yellow square
        '🟩', // Green square
        '🟪', // Purple square
        '🟧', // Orange square
        '🟥', // Red square
        '🟫', // Brown square
        '🔷', // Blue diamond
        '🔶', // Orange diamond
        '⭐'   // Star
    ];

    /**
     * Format time as "X minute(s) Y second(s)" for display
     * @param {number} ms - Time in milliseconds
     * @returns {string} Formatted time string
     */
    static formatTimeDescriptive(ms) {
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        const parts = [];
        if (minutes > 0) {
            parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
        }
        if (seconds > 0 || parts.length === 0) {
            parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
        }

        return parts.join(' ');
    }

    /**
     * Generate share text with optional category names (spoilers)
     * @param {TaxonomicGame} game - The game instance
     * @param {Object} options - Options for share text generation
     * @param {boolean} [options.includeLink=true] - Include game URL
     * @param {boolean} [options.includeSpoilers=false] - Include category names
     * @param {number} [options.completionTime] - Completion time in milliseconds
     */
    static generate(game, options = {}) {
        // Support legacy signature: generate(game, includeLink, includeSpoilers)
        if (typeof options === 'boolean') {
            const includeLink = options;
            const includeSpoilers = arguments[2] || false;
            options = { includeLink, includeSpoilers };
        }

        const { includeLink = true, includeSpoilers = false, completionTime } = options;

        const date = game.state.puzzleDate;
        const grid = this.generateGrid(game);

        let text = `Hodge Podge ${date} ✓\n\n${grid}`;

        if (includeSpoilers) {
            const categoryList = this.generateCategoryList(game);
            text += `\n\n${categoryList}`;
        }

        if (completionTime !== undefined) {
            text += `\n\nCompleted in ${this.formatTimeDescriptive(completionTime)}`;
        }

        if (includeLink) {
            text += `\n\nhttps://hodgepodgegame.com`;
        }

        return text;
    }

    /**
     * Generate category list with colored emojis
     */
    static generateCategoryList(game) {
        const lines = [];
        game.state.currentPuzzle.categories.forEach((category, idx) => {
            const emoji = this.CATEGORY_COLORS[idx];
            lines.push(`${emoji} ${category.answer}`);
        });
        return lines.join('\n');
    }

    /**
     * Generate colored grid showing category relationships
     */
    static generateGrid(game) {
        // Map category IDs to colors
        const categoryMap = {};
        game.state.currentPuzzle.categories.forEach((cat, idx) => {
            categoryMap[cat.id] = this.CATEGORY_COLORS[idx];
        });

        // Get original word order and map to categories
        const words = game.state.currentPuzzle.categories.flatMap(cat =>
            cat.words.map(w => ({ ...w, categoryId: cat.id }))
        );

        // Create dynamic NxN grid
        const visualGridSize = game.getVisualGridSize();
        const grid = [];
        for (let i = 0; i < words.length; i += visualGridSize) {
            const row = words.slice(i, i + visualGridSize)
                .map(w => categoryMap[w.categoryId])
                .join('');
            grid.push(row);
        }

        return grid.join('\n');
    }

    /**
     * Copy to clipboard
     */
    static async copyToClipboard(text) {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                return true;
            } else {
                // Fallback for older browsers
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                const success = document.execCommand('copy');
                document.body.removeChild(textarea);
                return success;
            }
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            return false;
        }
    }

    /**
     * Share using Web Share API if available
     */
    static async share(text) {
        if (navigator.share) {
            try {
                await navigator.share({
                    text: text
                });
                return true;
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Share failed:', error);
                }
                return false;
            }
        }
        return false;
    }
}
